// On-screen side of a Capital trip: the TRIP LEDGER (top-right, same look as the home HUD), urgent
// alerts from home (pigeon → smoke → war drums → red banner), and the "welcome home" tally.
// Everything shown here comes from capitalTrip.js, which only records things the game really did.

import { postMessage, resolveMessage } from './messageRail.js'
import { leaveAway } from '../game/awayState.js'
import gsap from 'gsap'
import { Events, on, emit } from './eventBus.js'
import { getTrip, homeStatus, pendingNet, currentRiskPerMin, graceLeftMs } from '../game/capitalTrip.js'
import { startPaidRepair, startLaborRepair } from '../game/damageManager.js'
import { RaidDamageConfig, WorkModes } from '../config/balance.js'
import { getWealth } from '../game/seasonManager.js'
import { formatCoins } from './format.js'
import { addCloseButton } from './domUtil.js'

const clock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
const signed = (n) => (n > 0 ? '+' : n < 0 ? '−' : '') + formatCoins(Math.abs(n))
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c])
const SEV = {
  whisper: { icon: '🕊', label: 'PIGEON' },
  smoke: { icon: '💨', label: 'SMOKE SIGNAL' },
  banner: { icon: '🚩', label: 'RED BANNER' },
  drums: { icon: '🥁', label: 'WAR DRUMS' },
}
const KIND_ICON = { gain: '▲', loss: '▼', home: '🏠', info: '·' }

export function mountCapitalTripUI() {
  if (document.querySelector('.trip-ledger')) return // idempotent

  // ── Ledger panel ──────────────────────────────────────────────────────────
  const ledger = document.createElement('aside')
  ledger.className = 'trip-ledger'
  ledger.setAttribute('aria-label', 'Trip ledger')
  document.body.appendChild(ledger)

  const renderLedger = () => {
    const t = getTrip()
    if (!t) { ledger.style.display = 'none'; return }
    ledger.style.display = 'block'
    const hs = homeStatus()
    const net = pendingNet()
    const risk = currentRiskPerMin()
    const grace = graceLeftMs()
    const riskTxt = grace > 0 ? `<b class="ok">None yet · ${clock(grace)}</b>`
      : risk <= 0 ? '<b class="ok">Quiet</b>'
      : risk < 0.35 ? '<b class="warn">Low</b>' : risk < 0.6 ? '<b class="warn">Rising</b>' : '<b class="bad">High</b>'
    const homeTxt = hs.raidActive ? '<b class="bad">Raid in progress!</b>'
      : hs.sites > 0 ? `<b class="warn">${hs.sites} ${hs.sites === 1 ? 'site' : 'sites'} damaged</b>` : '<b class="ok">Calm</b>'
    const postureTxt = hs.workMode === WorkModes.VILLAGE ? '<b class="ok">Guarding the village</b>' : '<b class="warn">In the fields</b>'
    const hungerTxt = { healthy: '<b class="ok">Fed</b>', skinny: '<b class="warn">Hungry</b>', dying: '<b class="bad">Starving</b>' }[hs.hunger]
    const entries = t.entries.slice(-6).reverse()
    ledger.innerHTML = `
      <div class="trip-ledger__head"><span>TRIP LEDGER <i>行程</i></span><span class="trip-ledger__clock">AWAY ${clock(Date.now() - t.startedAt)}</span></div>
      <div class="trip-ledger__net ${net > 0 ? 'up' : net < 0 ? 'down' : ''}">
        <span class="amt">${net === 0 ? '0m' : signed(net)}</span><small>from ${esc(t.place ?? 'the Capital')} · applied when you return</small>
      </div>
      <div class="trip-ledger__home">
        <div><span>Home</span>${homeTxt}</div>
        <div><span>Workers</span>${postureTxt}</div>
        <div><span>Hunger</span>${hungerTxt}</div>
        <div><span>Raid risk</span>${riskTxt}</div>
        <div><span>Treasury</span><b class="${hs.treasuryDelta > 0 ? 'ok' : hs.treasuryDelta < 0 ? 'bad' : ''}">${hs.treasuryDelta === 0 ? 'unchanged' : signed(hs.treasuryDelta) + ' at home'}</b></div>
      </div>
      <ol class="trip-ledger__entries">
        ${entries.map((e) => `<li class="${e.kind} ${e.severity ?? ''}"><i>${KIND_ICON[e.kind] ?? '·'}</i><span>${esc(e.text)}</span>${e.kind === 'gain' ? `<b>+${formatCoins(e.amount)}</b>` : e.kind === 'loss' ? `<b>−${formatCoins(e.amount)}</b>` : ''}</li>`).join('')}
      </ol>`
  }
  on(Events.TripLedgerChange, renderLedger)
  setInterval(() => { if (getTrip()) renderLedger() }, 1000)
  renderLedger()

  // ── Alerts: toasts (pigeon/smoke/banner) + the war-drums modal ─────────────
  const toasts = document.createElement('div')
  toasts.className = 'trip-toasts'
  toasts.setAttribute('aria-live', 'polite')
  document.body.appendChild(toasts)

  function showToast(entry) { // word from home while you are away: a message on the rail
    const sev = SEV[entry.severity] ?? SEV.whisper
    postMessage({ kind: entry.severity === 'banner' ? 'urgent' : 'info', type: `TRIP_${String(entry.severity).toUpperCase()}`, icon: sev.icon, title: sev.label, text: entry.text, lifeMs: entry.severity === 'banner' ? 9000 : 7000, pinned: false })
  }

  // Goons at the gates while you are away: an URGENT choice on the rail — it jumps the queue and stands until you choose.
  function showRaidModal(entry) {
    const hs = homeStatus()
    const posture = hs.workMode === WorkModes.VILLAGE
      ? 'Your workers are guarding the village — the paddies are exposed.'
      : 'Your workers are in the fields — the village is undefended.'
    postMessage({ key: 'trip-raid', kind: 'urgent', type: 'RAID', icon: '🥁', title: 'Urgent from the homestead 急报 · Goons at the gates!', text: entry.text,
      action: `${posture} You have been away ${clock(Date.now() - (getTrip()?.startedAt ?? Date.now()))}.`,
      choices: [{ label: 'RUSH HOME', primary: true, onPick: () => leaveAway() }, { label: 'RETURN LATER' }] })
  }
  on(Events.TripAlert, ({ entry }) => (entry.severity === 'drums' ? showRaidModal(entry) : showToast(entry)))
  on(Events.LeaveCapital, () => resolveMessage('trip-raid'))
  on(Events.LeaveLand, () => resolveMessage('trip-raid'))

  // ── Welcome home: the tally ────────────────────────────────────────────────
  on(Events.CapitalReturn, ({ summary }) => showReturn(summary))
}

function showReturn(s) {
  document.querySelector('.trip-modal--return')?.remove()
  const capital = s.entries.filter((e) => e.kind === 'gain' || e.kind === 'loss')
  const lines = []
  lines.push(s.raids > 0
    ? `${s.raids} ${s.raids === 1 ? 'raid struck' : 'raids struck'} while you were away.`
    : 'No raids struck while you were away.')
  if (s.sites > 0) lines.push(`${s.sites} ${s.sites === 1 ? 'site lies' : 'sites lie'} damaged.`)
  if (s.counters.catsLost) lines.push(`${s.counters.catsLost} of your cats ${s.counters.catsLost === 1 ? 'was' : 'were'} struck down.`)
  if (s.counters.injured) lines.push(`Your citizens were injured ${s.counters.injured} ${s.counters.injured === 1 ? 'time' : 'times'}.`)
  if (s.counters.converted) lines.push(`${s.counters.converted} ${s.counters.converted === 1 ? 'goon was' : 'goons were'} converted at your Temple.`)
  if (s.counters.starved) lines.push(`${s.counters.starved} ${s.counters.starved === 1 ? 'cat' : 'cats'} starved.`)
  if (s.homeDelta !== 0) lines.push(`Your treasury at home changed by ${signed(s.homeDelta)} (income and expenses while you were away).`)
  if (s.raidActive) lines.push('A raid is STILL under way — defend your village!')

  const el = document.createElement('div')
  el.className = 'trip-modal trip-modal--return'
  el.innerHTML = `
    <div class="trip-modal__card">
      <div class="trip-modal__kicker">归来 · WELCOME HOME</div>
      <h2>Back from ${esc(s.place ?? 'the Capital')}</h2>
      <p class="trip-modal__sub">You were away ${clock(s.durationMs)}.</p>
      <section><h3>In ${esc(s.place ?? 'the Capital')}</h3>
        ${capital.length
          ? `<ul class="trip-list">${capital.map((e) => `<li class="${e.kind}"><span>${esc(e.text)}</span><b>${e.kind === 'gain' ? '+' : '−'}${formatCoins(e.amount)}</b></li>`).join('')}</ul>`
          : '<p class="trip-none">You made no deals this time.</p>'}
      </section>
      <section><h3>At home</h3><ul class="trip-list plain">${lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul></section>
      <div class="trip-tally">
        <div class="trip-tally__label">TREASURY</div>
        <div class="trip-tally__num"><span data-from>${formatCoins(s.wealthBefore)}</span> <i>→</i> <b data-to class="${s.net > 0 ? 'up' : s.net < 0 ? 'down' : ''}">${formatCoins(s.wealthBefore)}</b></div>
        <div class="trip-tally__delta ${s.net > 0 ? 'up' : s.net < 0 ? 'down' : ''}" data-delta>${s.net === 0 ? `no change from ${esc(s.place ?? 'the Capital')}` : `${signed(s.net)} from ${esc(s.place ?? 'the Capital')}`}</div>
        ${s.lossAsked > s.loss ? `<small>(${formatCoins(s.lossAsked - s.loss)} of losses could not be collected — you didn't have it.)</small>` : ''}
      </div>
      ${s.sites > 0 ? `
      <div class="trip-repair">
        <div>Repairs owed: ${s.sites} × ${formatCoins(RaidDamageConfig.paidRepairCostPerDamage)} = <b>${formatCoins(s.repairCost)}</b></div>
        <div class="trip-modal__buttons">
          <button class="trip-btn trip-btn--primary" data-pay ${getWealth() < s.repairCost ? 'disabled' : ''}>PAY ${formatCoins(s.repairCost)}</button>
          <button class="trip-btn" data-labor>USE LABOR</button>
          <button class="trip-btn" data-later>LATER</button>
        </div>
      </div>` : ''}
      <div class="trip-modal__buttons"><button class="trip-btn ${s.sites > 0 ? '' : 'trip-btn--primary'}" data-ok>CONTINUE</button></div>
    </div>`
  document.body.appendChild(el)
  gsap.fromTo(el.querySelector('.trip-modal__card'), { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' })

  // Count the treasury up or down to its new total.
  const to = el.querySelector('[data-to]')
  const proxy = { v: s.wealthBefore }
  gsap.to(proxy, { v: s.wealthAfter, duration: s.net === 0 ? 0.01 : 1.8, delay: 0.5, ease: 'power2.out', onUpdate: () => { to.textContent = formatCoins(proxy.v) } })

  const close = () => el.remove()
  el.querySelector('[data-ok]').addEventListener('click', close)
  el.querySelector('[data-later]')?.addEventListener('click', () => el.querySelector('.trip-repair')?.remove())
  el.querySelector('[data-pay]')?.addEventListener('click', () => { if (startPaidRepair()) close() })
  el.querySelector('[data-labor]')?.addEventListener('click', () => { if (startLaborRepair()) close() })
  el.querySelector('[data-ok]').focus()
}
